import axios, { AxiosResponse, AxiosError } from 'axios';

const isDev = process.env.NODE_ENV === 'development';

// Configuration de l'API
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Instance Axios centralisée et partagée
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  timeout: 10000,
});

// Intercepteur requête : ajout automatique du token d'authentification
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Intercepteur réponse : gestion centralisée des erreurs 401
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      if (!globalThis.location.pathname.includes('/login')) {
        localStorage.removeItem('auth_token');
        globalThis.dispatchEvent(new CustomEvent('auth:logout'));
      }
    }
    return Promise.reject(error);
  }
);

// Types pour les requêtes API — payload d'envoi (camelCase)
export interface BookingRequestPayload {
  name: string;
  email: string;
  phone: string;
  serviceType?: string;
  dateTime?: string;
  message: string;
}

export interface BookingResponse {
  success: boolean;
  message: string;
  data?: {
    booking_id: number;
    email_status: unknown;
  };
  bookingId?: number;
}

export interface ApiError {
  error: string;
  message?: string;
  details?: Array<{
    field: string;
    message: string;
  }>;
}

export interface HealthResponse {
  status: string;
  timestamp: string;
  service: string;
  database: string;
  emailService: string;
}

// Classe d'erreur personnalisée
export class ApiException extends Error {
  public statusCode: number;
  public details?: Array<{ field: string; message: string }>;

  constructor(
    message: string, 
    statusCode: number, 
    details?: Array<{ field: string; message: string }>
  ) {
    super(message);
    this.name = 'ApiException';
    this.statusCode = statusCode;
    this.details = details;
  }
}

// Gestion centralisée des erreurs Axios
function handleApiError(error: unknown): never {
  if (isDev) {
    console.error('❌ API Error:', error);
  }

  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<Record<string, unknown>>;

    if (axiosError.response) {
      const { status, data: respData } = axiosError.response;
      if (isDev) {
        console.error(`❌ Server error ${status}:`, respData);
      }

      const errData = respData as Record<string, unknown>;
      throw new ApiException(
        (errData?.message as string) || (errData?.error as string) || 'Erreur du serveur',
        status,
        errData?.errors
          ? Object.entries(errData.errors as Record<string, unknown>).map(([field, message]) => ({
              field,
              message: Array.isArray(message) ? message[0] : String(message),
            }))
          : undefined
      );
    }

    if (axiosError.request) {
      if (isDev) {
        console.error('❌ Network error:', axiosError.request);
      }
      throw new ApiException(
        'Erreur de connexion. Vérifiez votre connexion internet.',
        0
      );
    }
  }

  throw new ApiException(
    'Une erreur inattendue s\'est produite.',
    500
  );
}

// Fonction utilitaire pour les appels API avec Axios
async function apiCall<T>(
  endpoint: string, 
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
  data?: unknown
): Promise<T> {
  try {
    if (isDev) {
      console.log(`🔍 API Call: ${method} ${endpoint}`);
      console.log('📤 Request data:', data);
    }

    const response: AxiosResponse<T> = await apiClient({
      url: endpoint,
      method,
      data,
    });

    if (isDev) {
      console.log('📥 Response status:', response.status);
      console.log('📥 Response data:', response.data);
    }

    return response.data;
  } catch (error) {
    handleApiError(error);
  }
}

// Services API
export const apiService = {
  async checkHealth(): Promise<HealthResponse> {
    return apiCall<HealthResponse>('/health', 'GET');
  },

  async createBookingRequest(data: BookingRequestPayload): Promise<BookingResponse> {
    if (isDev) {
      console.log('🚀 Creating booking request with data:', data);
    }
    
    const apiData = {
      name: data.name,
      email: data.email,
      phone: data.phone,
      service_type: data.serviceType,
      date_time: data.dateTime,
      message: data.message,
    };

    if (isDev) {
      console.log('📤 Sending to backend:', apiData);
    }

    const response = await apiCall<BookingResponse>('/booking-request', 'POST', apiData);

    if (response.data?.booking_id) {
      response.bookingId = response.data.booking_id;
    }

    if (isDev) {
      console.log('✅ Booking response received:', response);
    }
    return response;
  },

  async sendEmail(data: {
    to: string;
    subject: string;
    message: string;
    from?: string;
  }): Promise<{ success: boolean; message: string; id?: string }> {
    return apiCall('/send-email', 'POST', data);
  },
};

// Hook personnalisé pour les états de loading/error (optionnel)
export interface ApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export function createInitialApiState<T>(): ApiState<T> {
  return {
    data: null,
    loading: false,
    error: null,
  };
}
